import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, Heart, MapPin, Sparkles, 
  Clock, ChevronRight, Users, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';

export const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await api.get('/matches');
      setMatches(response.data);
    } catch (error) {
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = matches.filter(match => 
    match.user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatMatchDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 via-white to-pink-50" data-testid="loading-matches">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse"></div>
            <Heart className="w-10 h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-bounce" />
          </div>
          <p className="mt-6 text-lg font-medium text-gray-600">Loading your matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-pink-50 pb-24" data-testid="matches-page">
      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-gradient-to-b from-purple-50 to-transparent backdrop-blur-md">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-4">
          {/* Title with gradient */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-testid="matches-title">
                Your Matches
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Users className="w-4 h-4 text-purple-500" />
                <p className="text-gray-600 text-sm font-medium">{matches.length} connections</p>
              </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <Heart className="w-6 h-6 text-white" fill="white" />
            </div>
          </div>

          {/* Search Bar */}
          {matches.length > 0 && (
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search matches..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/80 backdrop-blur-sm border border-purple-100 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent shadow-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 mt-2">
        {matches.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
            data-testid="no-matches"
          >
            <div className="relative w-32 h-32 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full animate-pulse"></div>
              <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                <Heart className="w-14 h-14 text-purple-300" />
              </div>
              <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-400 animate-bounce" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No matches yet</h2>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto">
              Start swiping to discover amazing people waiting to connect with you!
            </p>
            <Button 
              onClick={() => navigate('/discover')} 
              className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 shadow-lg hover:shadow-xl transition-all"
              data-testid="start-swiping-button"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Swiping
            </Button>
          </motion.div>
        ) : (
          <AnimatePresence>
            <div className="space-y-4">
              {filteredMatches.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No matches found for "{searchQuery}"</p>
                </div>
              ) : (
                filteredMatches.map((match, index) => (
                  <motion.div
                    key={match.match_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`match-card-${match.match_id}`}
                    className="group"
                  >
                    <div 
                      className="bg-white rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-purple-50 hover:border-purple-100"
                      onClick={() => navigate(`/chat/${match.match_id}`)}
                    >
                      <div className="flex items-stretch">
                        {/* Profile Photo */}
                        <div className="relative w-28 sm:w-36 flex-shrink-0">
                          <img
                            src={match.user.photos?.[0] || 'https://images.unsplash.com/photo-1658909835269-e76abd3ffb5d?w=300'}
                            alt={match.user.name}
                            className="w-full h-full object-cover min-h-[140px]"
                          />
                          {/* Online indicator - optional */}
                          <div className="absolute bottom-2 left-2 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                        </div>

                        {/* Profile Info */}
                        <div className="flex-1 p-4 flex flex-col justify-between min-h-[140px]">
                          <div>
                            {/* Name and Age */}
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-lg text-gray-800" data-testid="match-name">
                                {match.user.name}
                              </h3>
                              <span className="text-gray-500 font-medium">{match.user.age}</span>
                            </div>

                            {/* Location if available */}
                            {match.user.location?.city && (
                              <div className="flex items-center gap-1 text-gray-400 text-sm mb-2">
                                <MapPin className="w-3.5 h-3.5" />
                                <span>{match.user.location.city}</span>
                              </div>
                            )}

                            {/* Bio */}
                            <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                              {match.user.bio || 'No bio yet'}
                            </p>
                          </div>

                          {/* Bottom row: Match date + Chat button */}
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                              <Clock className="w-3.5 h-3.5" />
                              <span>Matched {formatMatchDate(match.matched_at)}</span>
                            </div>
                            
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/chat/${match.match_id}`);
                              }}
                              className="rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white shadow-md hover:shadow-lg transition-all px-4 py-2 h-auto"
                              data-testid={`chat-button-${match.match_id}`}
                            >
                              <MessageCircle className="w-4 h-4 mr-1.5" />
                              Chat
                            </Button>
                          </div>
                        </div>

                        {/* Chevron indicator */}
                        <div className="flex items-center pr-3 text-gray-300 group-hover:text-purple-400 transition-colors">
                          <ChevronRight className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </AnimatePresence>
        )}

        {/* Quick Stats Footer */}
        {matches.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 mb-4 text-center"
          >
            <p className="text-sm text-gray-400">
              💕 Keep swiping to find more connections!
            </p>
            <Button
              variant="ghost"
              onClick={() => navigate('/discover')}
              className="mt-2 text-purple-500 hover:text-purple-600 hover:bg-purple-50 rounded-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Discover More
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};